import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { inventarioAPI, reportesAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const Inventario = () => {
  const { categorias, marcas, proveedores, formatCurrency } = useApp();
  const { modoEspectador } = useAuth();
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    buscar: '', categoria: '', marca: '', proveedor: '',
    estado_stock: '', precio_min: '', precio_max: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAjuste, setShowAjuste] = useState(null);
  const [ajusteData, setAjusteData] = useState({ tipo: 'entrada', cantidad: '', motivo: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 20, page };
      if (filtros.buscar) params.buscar = filtros.buscar;
      if (filtros.categoria) params.categoria = filtros.categoria;
      if (filtros.marca) params.marca = filtros.marca;
      if (filtros.proveedor) params.proveedor = filtros.proveedor;
      if (filtros.estado_stock) params.estado_stock = filtros.estado_stock;

      const res = await inventarioAPI.getAll(params);
      if (res.data.success) {
        let data = res.data.data;
        if (filtros.precio_min) data = data.filter(item => item.precio_venta >= parseFloat(filtros.precio_min));
        if (filtros.precio_max) data = data.filter(item => item.precio_venta <= parseFloat(filtros.precio_max));
        setItems(data);
        setResumen(res.data.resumen);
        setTotal(res.data.total || data.length);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (e) { toast.error('Error'); }
    finally { setLoading(false); }
  }, [filtros, page]);

  useEffect(() => { load(); }, [load]);

  const handleFiltroChange = (e) => { setPage(1); setFiltros(prev => ({ ...prev, [e.target.name]: e.target.value })); };

  const limpiarFiltros = () => { setPage(1); setFiltros({ buscar: '', categoria: '', marca: '', proveedor: '', estado_stock: '', precio_min: '', precio_max: '' }); };

  const handleAjuste = async (e) => {
    e.preventDefault();
    if (!ajusteData.cantidad || ajusteData.cantidad <= 0) { toast.error('Cantidad inválida'); return; }
    try {
      await inventarioAPI.ajustarStock(showAjuste.id, { tipo: ajusteData.tipo, cantidad: parseInt(ajusteData.cantidad), motivo: ajusteData.motivo || 'Ajuste manual' });
      toast.success('Stock ajustado correctamente'); setShowAjuste(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const exportar = async (tipo) => {
    try {
      const res = tipo === 'excel' ? await reportesAPI.exportarExcel(filtros) : await reportesAPI.exportarPDF(filtros);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `inventario.${tipo === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success(`${tipo.toUpperCase()} exportado`);
    } catch (e) { toast.error('Error al exportar'); }
  };

  const getEstado = (e) => ({ sin_stock: { c: 'sin-stock', t: 'Sin Stock' }, bajo: { c: 'bajo', t: 'Stock Bajo' }, normal: { c: 'normal', t: 'Normal' }, alto: { c: 'alto', t: 'Stock Alto' } }[e] || { c: 'normal', t: 'Normal' });

  const hayFiltros = filtros.buscar || filtros.categoria || filtros.marca || filtros.proveedor || filtros.estado_stock || filtros.precio_min || filtros.precio_max;

  return (
    <div>
      {/* Resumen */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="stat-card"><div className="d-flex justify-content-between"><div><p className="stat-label mb-1">Total Productos</p><h4 className="stat-value mb-0">{resumen.total_productos || 0}</h4></div><div className="stat-icon bg-primary bg-opacity-10 text-primary"><i className="bi bi-box-seam"></i></div></div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-card"><div className="d-flex justify-content-between"><div><p className="stat-label mb-1">Sin Stock</p><h4 className="stat-value mb-0 text-danger">{resumen.sin_stock || 0}</h4></div><div className="stat-icon bg-danger bg-opacity-10 text-danger"><i className="bi bi-exclamation-circle"></i></div></div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-card"><div className="d-flex justify-content-between"><div><p className="stat-label mb-1">Stock Bajo</p><h4 className="stat-value mb-0 text-warning">{resumen.stock_bajo || 0}</h4></div><div className="stat-icon bg-warning bg-opacity-10 text-warning"><i className="bi bi-exclamation-triangle"></i></div></div></div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-card"><div className="d-flex justify-content-between"><div><p className="stat-label mb-1">Valor Total</p><h4 className="stat-value mb-0 text-success">{formatCurrency(resumen.valor_inventario || 0)}</h4></div><div className="stat-icon bg-success bg-opacity-10 text-success"><i className="bi bi-currency-dollar"></i></div></div></div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card mb-4"><div className="card-body">
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Buscar</label>
            <input type="text" className="form-control" placeholder="Código, nombre..." name="buscar" value={filtros.buscar} onChange={handleFiltroChange} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Categoría</label>
            <select className="form-select" name="categoria" value={filtros.categoria} onChange={handleFiltroChange}>
              <option value="">Todas</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Marca</label>
            <select className="form-select" name="marca" value={filtros.marca} onChange={handleFiltroChange}>
              <option value="">Todas</option>
              {marcas.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Estado</label>
            <select className="form-select" name="estado_stock" value={filtros.estado_stock} onChange={handleFiltroChange}>
              <option value="">Todos</option>
              <option value="sin_stock">Sin Stock</option>
              <option value="bajo">Stock Bajo</option>
              <option value="normal">Normal</option>
              <option value="alto">Stock Alto</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Rango Precio</label>
            <div className="input-group">
              <input type="number" className="form-control" placeholder="Min" name="precio_min" value={filtros.precio_min} onChange={handleFiltroChange} />
              <span className="input-group-text">-</span>
              <input type="number" className="form-control" placeholder="Max" name="precio_max" value={filtros.precio_max} onChange={handleFiltroChange} />
            </div>
          </div>
        </div>
        <div className="row g-3 mt-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Proveedor</label>
            <select className="form-select" name="proveedor" value={filtros.proveedor} onChange={handleFiltroChange}>
              <option value="">Todos</option>
              {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>
          <div className="col-md-9">
            <div className="d-flex gap-2 justify-content-end">
              {hayFiltros && <button className="btn btn-outline-secondary" onClick={limpiarFiltros}><i className="bi bi-x-lg me-1"></i>Limpiar</button>}
              <button className="btn btn-outline-success" onClick={() => exportar('excel')}><i className="bi bi-file-earmark-excel me-1"></i>Excel</button>
              <button className="btn btn-outline-danger" onClick={() => exportar('pdf')}><i className="bi bi-file-earmark-pdf me-1"></i>PDF</button>
            </div>
          </div>
        </div>
      </div></div>

      {/* Tabla */}
      <div className="card"><div className="card-body p-0">
        {loading ? <div className="loading-container"><div className="spinner-border text-primary"></div></div>
        : items.length === 0 ? (
          <div className="empty-state py-5">
            <i className="bi bi-clipboard-data"></i>
            <h5>No hay productos{hayFiltros ? ' con los filtros seleccionados' : ''}</h5>
            {hayFiltros && <button className="btn btn-outline-primary mt-3" onClick={limpiarFiltros}>Limpiar Filtros</button>}
          </div>
        ) : (
          <div className="table-responsive"><table className="table table-hover mb-0">
            <thead><tr><th>Código</th><th>Producto</th><th>Categoría</th><th>Marca</th><th className="text-end">P.Compra</th><th className="text-end">P.Venta</th><th className="text-center">Stock</th><th className="text-center">Estado</th><th className="text-end">Valor</th><th>Acciones</th></tr></thead>
            <tbody>
              {items.map(item => {
                const estado = getEstado(item.estado_stock);
                return (
                  <tr key={item.id}>
                    <td><code>{item.codigo}</code></td>
                    <td>{item.nombre}</td>
                    <td>{item.categoria || '-'}</td>
                    <td>{item.marca || '-'}</td>
                    <td className="text-end">{formatCurrency(item.precio_compra)}</td>
                    <td className="text-end">{formatCurrency(item.precio_venta)}</td>
                    <td className="text-center"><strong>{item.stock_actual}</strong><br/><small className="text-muted">min:{item.stock_minimo}</small></td>
                    <td className="text-center"><span className={`badge-stock ${estado.c}`}>{estado.t}</span></td>
                    <td className="text-end">{formatCurrency(item.stock_actual * item.precio_compra)}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-primary" title="Ajustar" disabled={modoEspectador} onClick={() => { setShowAjuste(item); setAjusteData({ tipo: 'entrada', cantidad: '', motivo: '' }); }}><i className="bi bi-plus-slash-minus"></i></button>
                        <Link to={`/kardex/${item.id}`} className="btn btn-outline-info" title="Kardex"><i className="bi bi-clock-history"></i></Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
      {items.length > 0 && (
        <div className="card-footer d-flex justify-content-between align-items-center">
          <span className="text-muted">Mostrando {items.length} de {total} producto(s)</span>
          {totalPages > 1 && (
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => p - 1)}>Anterior</button>
                </li>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pg;
                  if (totalPages <= 5) pg = i + 1;
                  else if (page <= 3) pg = i + 1;
                  else if (page >= totalPages - 2) pg = totalPages - 4 + i;
                  else pg = page - 2 + i;
                  return (
                    <li key={pg} className={`page-item ${page === pg ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(pg)}>{pg}</button>
                    </li>
                  );
                })}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p => p + 1)}>Siguiente</button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      )}
      </div>

      {/* Modal Ajuste */}
      {showAjuste && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog"><div className="modal-content">
            <div className="modal-header"><h5 className="modal-title"><i className="bi bi-plus-slash-minus me-2"></i>Ajustar: {showAjuste.nombre}</h5><button className="btn-close" onClick={() => setShowAjuste(null)}></button></div>
            <form onSubmit={handleAjuste}>
              <div className="modal-body">
                <div className="alert alert-info mb-3"><strong>Stock actual:</strong> {showAjuste.stock_actual} unidades</div>
                <div className="mb-3">
                  <label className="form-label">Tipo de Movimiento</label>
                  <div className="btn-group w-100">
                    <button type="button" className={`btn ${ajusteData.tipo === 'entrada' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setAjusteData({...ajusteData, tipo: 'entrada'})}><i className="bi bi-plus-lg me-2"></i>Entrada</button>
                    <button type="button" className={`btn ${ajusteData.tipo === 'salida' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => setAjusteData({...ajusteData, tipo: 'salida'})}><i className="bi bi-dash-lg me-2"></i>Salida</button>
                  </div>
                </div>
                <div className="mb-3"><label className="form-label">Cantidad</label><input type="number" className="form-control" min="1" value={ajusteData.cantidad} onChange={e => setAjusteData({...ajusteData, cantidad: e.target.value})} required /></div>
                <div className="mb-3"><label className="form-label">Motivo</label><input type="text" className="form-control" value={ajusteData.motivo} onChange={e => setAjusteData({...ajusteData, motivo: e.target.value})} placeholder="Ej: Ajuste por inventario físico" /></div>
                {ajusteData.cantidad && <div className={`alert alert-${ajusteData.tipo === 'entrada' ? 'success' : 'warning'}`}><strong>Stock resultante:</strong> {ajusteData.tipo === 'entrada' ? showAjuste.stock_actual + parseInt(ajusteData.cantidad || 0) : showAjuste.stock_actual - parseInt(ajusteData.cantidad || 0)} unidades</div>}
              </div>
              <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={() => setShowAjuste(null)}>Cancelar</button><button type="submit" className="btn btn-primary"><i className="bi bi-check-lg me-2"></i>Aplicar</button></div>
            </form>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default Inventario;
